import { gql } from '@apollo/client';

/**
 * Spec 0016 (дипломы студентов) — ERP-scope mutations. Operation names are
 * deliberately different from the backend field names (`uploadStudentDiploma`,
 * `deleteStudentDiploma`, `matchDiplomaFiles`) to avoid the codegen
 * result-type collapse.
 */

/**
 * Attaches one PDF to one student's PL/EN slot. `file` travels as an `Upload`
 * scalar over the GraphQL multipart request spec — which is why the Apollo
 * terminating link is `createUploadLink` (see `core/graphql/client/client.ts`).
 *
 * `replaceExisting` must be sent as `true` only for a slot the operator
 * explicitly confirmed as a replacement; the backend otherwise refuses to
 * overwrite an already-attached diploma.
 */
export const UploadStudentDiplomaMutation = gql`
  mutation UploadDiplomaFile(
    $studentId: ID!
    $groupId: ID!
    $language: DiplomaLanguageEnum!
    $file: Upload!
    $replaceExisting: Boolean
  ) {
    uploadStudentDiploma(
      studentId: $studentId
      groupId: $groupId
      language: $language
      file: $file
      replaceExisting: $replaceExisting
    ) {
      success
      customErrors {
        field
        code
        message
      }
      diploma {
        id
        pk
        language
        source
        originalName
        contentType
        sizeBytes
        version
        uploadedAt
        uploadedByName
      }
    }
  }
`;

export const DeleteStudentDiplomaMutation = gql`
  mutation RemoveDiplomaFile($diplomaId: ID!) {
    deleteStudentDiploma(diplomaId: $diplomaId) {
      success
      ok
      customErrors {
        field
        code
        message
      }
    }
  }
`;

/**
 * Dry-run for the bulk attach: takes the picked files' NAMES ONLY (no bytes)
 * and returns the suggested student/language per file, plus the students the
 * batch would miss. Nothing is written — the actual upload happens afterwards,
 * one `UploadDiplomaFile` per file, only for rows the operator confirmed.
 */
export const MatchDiplomaFilesMutation = gql`
  mutation PreviewDiplomaMatch($groupId: ID!, $fileNames: [String!]!) {
    matchDiplomaFiles(groupId: $groupId, fileNames: $fileNames) {
      success
      customErrors {
        field
        code
        message
      }
      preview {
        summary {
          total
          matched
          ambiguous
          noMatch
          duplicates
          willReplace
        }
        rows {
          fileName
          status
          language
          matchedBy
          confidence
          student {
            id
            firstName
            lastName
            fullName
            email
          }
          candidates {
            fullName
            student {
              id
              firstName
              lastName
              fullName
              email
            }
          }
          wouldReplace {
            id
            language
            originalName
            version
            uploadedAt
          }
        }
        unmatchedStudents {
          fullName
          missingLanguages
          student {
            id
            firstName
            lastName
            fullName
            email
          }
        }
      }
    }
  }
`;
